import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import useFormSectionTiming, {
  type SectionMs,
} from '~hooks/useAnalytics/useFormSectionTiming';
import type { ProjectFormDirtyFields } from '~hooks/useProjects/models';
import { ProjectFormHandle } from '~pages/Sales/Projects/projectFormHandle';

import type { ProjectFormSchemaInterface } from '../projectFormSchema';
import { buildQuotePreviewInput } from './buildQuotePreviewInput';
import { ContactsPanel } from './panels/ContactsPanel';
import { DateAndTimePanel } from './panels/DateAndTimePanel';
import { FileAttachmentsPanel } from './panels/FileAttachmentsPanel';
import { FreightRatesPanel } from './panels/FreightRatesPanel';
import { LocationDetailsPanel } from './panels/LocationDetailsPanel';
import { MaterialsPanel } from './panels/MaterialsPanel';
import { NotesPanel } from './panels/NotesPanel';
import { OrderDefaultsPanel } from './panels/OrderDefaultsPanel';
import { ProjectDetailsPanel } from './panels/ProjectDetailsPanel';
import { QuotePdfTermsPanel } from './panels/QuotePdfTermsPanel';
import { RequesterPanel } from './panels/RequesterPanel';
import { TaxPanel } from './panels/TaxPanel';
import { useProjectFormV2Context } from './ProjectFormV2Context';

/**
 * Imperative handle exposed by the v2 form shell. Extends the legacy
 * ProjectFormHandle with `getDirtyFields()` so the dialog can pass
 * RHF's dirtyFields snapshot into deparseUpdateV2 at submit time. Legacy
 * implementations of ProjectFormHandle need not provide
 * `getDirtyFields` — the dialog uses optional-chaining and falls back to
 * the legacy full-PATCH path when undefined.
 */
export type ProjectFormV2Handle = ProjectFormHandle & {
  getDirtyFields: () => ProjectFormDirtyFields;
  // Live RHF `isDirty` snapshot. The dialog reads this on close to decide
  // whether to raise the "Are you sure you want to exit?" confirmation —
  // legacy ProjectForm reports the same signal via onFormStateChange,
  // but the v2 shell is effect-free, so the dialog
  // pulls it from the handle at close time instead of being pushed updates.
  getIsDirty: () => boolean;
  getQuotePreviewInput: () => ReturnType<typeof buildQuotePreviewInput>;
  getSectionMs: () => SectionMs;
};

const MATERIAL_RATES_SELECTOR = '[data-test-id^="project-form-v2-material-rates-"]';

// Thin render shell. The Provider OWNS useForm and renders <FormProvider>
// (see ProjectFormV2Context.tsx). The shell consumes via useFormContext and
// exposes the imperative handle the dialog already wires against.
//
// Async errors (props.errors on the dialog) are forwarded into the Provider
// directly so the inline-error mapping effect lives there (the shell is
// effect-free per the panel-grep gate).
export const ProjectFormV2 = forwardRef<ProjectFormV2Handle>(
  function ProjectFormV2(_props, ref) {
    const { handleSubmit, getValues, formState, reset } =
      useFormContext<ProjectFormSchemaInterface>();
    const { defaultProject, fileAttachmentsRef, freightRatesRef, isCopyAndEdit } =
      useProjectFormV2Context();
    const { focusSection, getSectionMs, pauseWhenFocusLeavesForm } = useFormSectionTiming(
      'project_form_v2',
      Boolean(defaultProject?.id) && !isCopyAndEdit,
    );

    // Subscribe to `isDirty` and `dirtyFields` during render so the handle's
    // getIsDirty()/getDirtyFields() reads below are accurate. RHF's formState
    // is a lazy proxy: in isolation, an unsubscribed `isDirty` is never
    // recomputed and an unsubscribed `dirtyFields` stays frozen at `{}`, so a
    // handle that only read them at call time would hand deparseUpdateV2 an
    // empty snapshot. In practice the Provider's yupResolver (mode: 'onChange')
    // recomputes formState on every edit and keeps both live regardless — so
    // this subscription is defensive, not load-bearing today. We keep it so the
    // handle's contract stays self-contained instead of silently depending on
    // the resolver config; if the resolver/mode ever changes, the handle keeps
    // working.
    const { isDirty, dirtyFields } = formState;

    // Dedupe gate: prior Ralph attempts and the legacy
    // useImperativeHandle have produced
    // double-submits. handleSubmit by itself does NOT deduplicate
    // synchronous double-invokes (both promises race through validation and
    // both fire onValid). The ref guards against that race so the dialog can
    // safely wire onClick handlers without worrying about rapid clicks.
    const isSubmittingRef = useRef(false);

    const submit = useCallback(
      async (callBack?: Parameters<ProjectFormHandle['submit']>[0]): Promise<void> => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        try {
          // Extract from the rule-based <FreightRates/> UI via the
          // Provider-owned ref. The dialog's submit pipeline consumes:
          //   - siteRateScheduleOperations → processSiteRateScheduleOperationsResilient
          //   - customer/vendor/driver RateAddOnAssignments → processAddOnAssignmentsForProject
          // Without this extraction the dialog gets `undefined` for all 4
          // and nothing from the Freight Rates block ever reaches the API.
          //
          // The retired Customer/Vendor/Driver triple UI is NOT supported
          // in v2 — we do NOT write the 9 side-channel rate fields
          // (customerRate*/vendorRate*/driverRate*) back into form state here.
          let rateExtract: {
            customerRateAddOnAssignments: unknown[];
            vendorRateAddOnAssignments: unknown[];
            driverRateAddOnAssignments: unknown[];
            siteRateScheduleOperations:
              | { toCreate: unknown[]; toUpdate: unknown[]; toDelete: unknown[] }
              | undefined;
          } = {
            customerRateAddOnAssignments: [],
            vendorRateAddOnAssignments: [],
            driverRateAddOnAssignments: [],
            siteRateScheduleOperations: {
              toCreate: [],
              toUpdate: [],
              toDelete: [],
            },
          };

          if (freightRatesRef.current?.onSubmit) {
            // Await the handle's result, NOT just the callback. onSubmit only
            // invokes the callback when freight validation passes and resolves
            // `false` when it fails. Waiting on the callback alone would hang
            // forever on a validation failure, and because that await sits
            // inside the isSubmittingRef guard, the `finally` below would
            // never run — leaving the form permanently unable to resubmit until
            // a remount. Returning early here keeps the guard release intact so
            // the user can fix the invalid rows and submit again.
            const isFreightValid = await freightRatesRef.current.onSubmit((data) => {
              rateExtract = {
                customerRateAddOnAssignments: data.customerRateAddOnAssignments,
                vendorRateAddOnAssignments: data.vendorRateAddOnAssignments,
                driverRateAddOnAssignments: data.driverRateAddOnAssignments,
                siteRateScheduleOperations: data.siteRateScheduleOperations,
              };
            });
            if (!isFreightValid) {
              AnalyticsController.track('project_form_v2_validation_failed');
              return;
            }
          }

          await handleSubmit(
            (data) =>
              callBack?.(
                data,
                rateExtract.customerRateAddOnAssignments as never,
                rateExtract.vendorRateAddOnAssignments as never,
                rateExtract.driverRateAddOnAssignments as never,
                rateExtract.siteRateScheduleOperations as never,
                getValues('projectMaterialTotals') ?? undefined,
              ),
            () => {
              AnalyticsController.track('project_form_v2_validation_failed');
            },
          )();
        } finally {
          isSubmittingRef.current = false;
        }
      },
      [handleSubmit, getValues, freightRatesRef],
    );

    useImperativeHandle(
      ref,
      () => ({
        submit,
        resetForm: (callBack) => {
          reset();
          callBack?.();
        },
        // Flush the queued attachments via the Context-owned ref (written by
        // the FileAttachmentsPanel). Mirrors the legacy form.
        // The dialog calls fileAttachmentsOnSubmit(project.id) in onSuccess and
        // fileAttachmentsOnClose() on cancel; the optional-chain keeps both
        // flows resolving cleanly if the panel hasn't mounted the widget yet.
        fileAttachmentsOnClose: async () => {
          await fileAttachmentsRef.current?.onCancel();
        },
        fileAttachmentsOnSubmit: async (projectId: string) => {
          await fileAttachmentsRef.current?.onSubmit(projectId);
        },
        // Delegate to the FreightRates component (mounted by the
        // FreightRatesPanel and wired through the Context-owned ref). Mirrors
        // the legacy form. After a partial site-rate-schedule
        // save, the dialog (ProjectDialog) calls this with the per-row save
        // result; FreightRates.applySaveResult paints the per-card error
        // markers + settled server IDs back into the rows AND returns the
        // { hasFailures, succeededCount, failedCount } summary the dialog uses
        // to report status and decide whether to keep the dialog open for the
        // user to fix failed rows. The optional-chain keeps it resolving to
        // undefined if the freight widget hasn't mounted yet (the dialog
        // already handles that case).
        applySiteRateSaveResult: (result) =>
          freightRatesRef.current?.applySaveResult?.(result),
        // Snapshot of RHF's dirtyFields at call time. The dialog passes the
        // snapshot into deparseUpdateV2 so the PATCH carries only
        // the user-touched fields. RHF's dirtyFields is a lazy proxy whose
        // value-type is DeepMap<DeepPartial<TFieldValues>, boolean> — booleans
        // for primitives, nested objects/arrays for object/array fields. The
        // V2 deparser's `ProjectFormDirtyFields` shape declares value as
        // `unknown` precisely so it can absorb either; truthiness is the only
        // contract.
        getDirtyFields: () => dirtyFields,
        getIsDirty: () => isDirty,
        getQuotePreviewInput: () => {
          // A quote that silently reports zero freight is worse than no quote,
          // so an unreachable rate-card reader is surfaced rather than
          // defaulted to an empty list.
          const getSiteRates = freightRatesRef.current?.getSiteRates;
          if (!getSiteRates) {
            throw new Error('Rate cards are unavailable for the quote preview');
          }
          return buildQuotePreviewInput(getValues(), getSiteRates());
        },
        getSectionMs,
      }),
      [
        submit,
        reset,
        formState,
        isDirty,
        dirtyFields,
        fileAttachmentsRef,
        freightRatesRef,
        getValues,
        getSectionMs,
      ],
    );

    // Structural skeleton: two-column grid mirroring the legacy form.
    // All 12 panels live here.
    return (
      <Box
        data-test-id="project-form-v2"
        onBlur={pauseWhenFocusLeavesForm}
        onFocus={(event) => focusSection(null, event)}
      >
        <Grid container spacing={2} sx={{ mb: 1, mt: 3 }}>
          <Grid size={{ xs: 6 }}>
            <ProjectDetailsPanel />
            <DateAndTimePanel />
            <Box
              onFocus={(event) =>
                focusSection(
                  event.target.closest(MATERIAL_RATES_SELECTOR)
                    ? 'material_rates'
                    : 'materials',
                  event,
                )
              }
            >
              <MaterialsPanel />
            </Box>
            <Box onFocus={(event) => focusSection('freight_rates', event)}>
              <FreightRatesPanel />
            </Box>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Box onFocus={(event) => focusSection('location', event)}>
              <LocationDetailsPanel />
            </Box>
            <Box onFocus={(event) => focusSection('order_defaults', event)}>
              <OrderDefaultsPanel />
            </Box>
            <TaxPanel />
            <Box onFocus={(event) => focusSection('contacts', event)}>
              <ContactsPanel />
            </Box>
            <FileAttachmentsPanel />
            <NotesPanel />
            <QuotePdfTermsPanel />
            <RequesterPanel />
          </Grid>
        </Grid>
      </Box>
    );
  },
);
